import _ from 'lodash';
import React from 'react';
import {
    MutationFunctionOptions,
    MutationTuple,
} from '/@src/utils/error-handling/apollo-hooks-wrappers/custom-apollo-hooks';
import {
    RATE_LIMIT_TYPE,
    useRateLimitedCallback,
} from '../../utils/rate-limit.util';

/*
 * Wrapper for mutations which allows you to rate limit them, either using a debounce or throttle.
 * You also have the ability to trigger an immediate mutation at any time, and can control whether
 * doing so should also automatically cancel any pending rate-limited mutation.
 *
 * NOTE: For this to work correctly, the reference passed for mutationHookResult should NOT change
 * often (eg every render). Each time the reference changes, the rate limit tracking will get
 * reset, so you must use useMemo or useCallback to ensure you pass the same value whenever it is
 * not changing.
 *
 * NOTE: Ensure all variables for the request are fetched and encapsulated BEFORE wrapping it with
 * this hook. You should avoid cases where some variables for the request are fetched before the
 * rate limiting, and others are fetched after the rate limiting, because there is a chance the
 * data can change during the rate limit delay period, resulting in an inconsistent set of
 * variables in the final request. This also applies to variables which may get automatically
 * injected, such as session information.
 */
export function useRateLimitedMutation<TData, TVariables>(
    mutationHookResult: MutationTuple<TData, TVariables>,
    rateLimitType: RATE_LIMIT_TYPE,
    rateLimitPeriodMs: number,
    shouldCancelRateLimitedMutationOnImmediate = true,
): [
    MutationTuple<TData, TVariables>[0],
    MutationTuple<TData, TVariables>[0],
    () => void,
    MutationTuple<TData, TVariables>[1],
] {
    const [mutate, state] = mutationHookResult;

    const mutateRateLimited = useRateLimitedCallback(
        mutate,
        rateLimitType,
        rateLimitPeriodMs,
    );

    // Cancel any pending mutation as a cleanup step when unmounting
    React.useEffect(() => {
        return () => {
            mutateRateLimited.cancel();
        };
    }, []);

    // For the immediate mutation, check if we want any immediate mutation to also cancel any
    // pending rate limited mutation before it. We will create the immediate mutation callback with
    // that expected behavior in mind.
    const mutateImmediate = React.useMemo(() => {
        if (!shouldCancelRateLimitedMutationOnImmediate) {
            return mutate;
        }

        return (
            mutateOptions: MutationFunctionOptions<TData, TVariables> = {},
        ) => {
            mutateRateLimited.cancel();
            return mutate(mutateOptions);
        };
    }, [shouldCancelRateLimitedMutationOnImmediate, mutate, mutateRateLimited]);

    return [
        mutateImmediate,
        mutateRateLimited,
        mutateRateLimited.cancel,
        state,
    ];
}
